import * as Yup from 'yup';
import type { RequestCommandUpdateFirmware } from '../app/ApiGen';
import { createSchema } from './formUtils';

// OCPP retries/retryInterval are optional server-side, but the form always carries them as
// strings (empty when unset) so destructuring onSubmit values stays narrow.
export type UpdateFirmwareFields = {
  firmwareUrl: RequestCommandUpdateFirmware['location'];
  retries: string;
  retryInterval: string;
};

const EvnexChargePointFirmwareUrlRegEx =
  '^(https://)?(www.)?s3.ap-southeast-2.amazonaws.com/(history.)?firmware.evnex.io/([-a-zA-Z0-9()@:%_+.~#?&//=]*)';

const urlValidation = Yup.string().typeError('URL must be a string').required('Firmware URL is required');

// OCPP retries/retryInterval are optional integers within a bounded range. We carry them as
// strings through the form and convert on submit so empty inputs round-trip cleanly.
export const RETRIES_MIN = 1;
export const RETRIES_MAX = 3;
export const RETRY_INTERVAL_MIN_SECONDS = 15;
export const RETRY_INTERVAL_MAX_SECONDS = 300;

const integerInRange = (fieldLabel: string, min: number, max: number) =>
  Yup.string().test({
    name: `${fieldLabel.replace(/\s+/g, '-').toLowerCase()}-range`,
    message: `${fieldLabel} must be an integer between ${min} and ${max}`,
    test: (value: unknown) => {
      if (value === undefined || value === null) return true;
      const trimmed = typeof value === 'string' ? value.trim() : String(value).trim();
      if (trimmed === '') return true;
      if (!/^\d+$/.test(trimmed)) return false;
      const parsed = Number(trimmed);
      return parsed >= min && parsed <= max;
    },
  });

export function validationSchema(args: {
  chargePointVendor: string | undefined;
}): Yup.ObjectSchema<Partial<UpdateFirmwareFields>, UpdateFirmwareFields> {
  const { chargePointVendor } = args;

  const firmwareUrl =
    chargePointVendor === 'Evnex'
      ? urlValidation.matches(new RegExp(EvnexChargePointFirmwareUrlRegEx), 'Unsupported URL')
      : urlValidation;

  return createSchema<UpdateFirmwareFields>({
    firmwareUrl,
    retries: integerInRange('Retries', RETRIES_MIN, RETRIES_MAX),
    retryInterval: integerInRange('Retry interval', RETRY_INTERVAL_MIN_SECONDS, RETRY_INTERVAL_MAX_SECONDS),
  });
}

export function parseOptionalInt(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
